/**
 * Sparkline — clean SVG chart with a smooth line, subtle grid, and minimal labels.
 */
import { useId } from 'react';
import { smoothPath } from '../utils/chart';

export function Sparkline({ data, color, height = 90, unit }: { data: { date: string; value: number }[]; color: string; height?: number; unit?: string }) {
  const reactId = useId();
  if (data.length < 2) return null;
  // Deduplicate same-date points by averaging (prevents division by zero in smoothPath)
  const byDate = new Map<string, number[]>();
  for (const d of data) { const arr = byDate.get(d.date) || []; arr.push(d.value); byDate.set(d.date, arr); }
  const sorted = [...byDate.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, vals]) => ({ date, value: vals.reduce((s, v) => s + v, 0) / vals.length }));
  if (sorted.length < 2) return null;
  const values = sorted.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.08 || 1;
  const yMin = min - pad;
  const yMax = max + pad;
  const range = yMax - yMin;

  const svgW = 320;
  const labelW = 48;
  const padTop = 8;
  const padBot = 20;
  const chartW = svgW - labelW - 8;
  const chartH = height - padTop - padBot;

  const pts = sorted.map((d, i) => ({
    x: labelW + (i / (sorted.length - 1)) * chartW,
    y: padTop + chartH - ((d.value - yMin) / range) * chartH,
    date: d.date,
    value: d.value,
  }));

  const gradientId = `sg-${color.replace(/[^a-z0-9]/gi, '')}-${reactId.replace(/[^a-z0-9]/gi, '')}`;
  const linePath = smoothPath(pts);
  const areaPath = `${linePath}L${pts[pts.length - 1].x},${padTop + chartH}L${pts[0].x},${padTop + chartH}Z`;

  const fmt = (v: number) => v >= 10000 ? `${(v / 1000).toFixed(0)}k` : v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v % 1 === 0 ? String(v) : v.toFixed(1);
  const fmtDate = (d: string) => { const dt = new Date(d + 'T12:00:00'); return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); };

  const gridLines = [0, 0.5, 1].map(f => {
    const val = yMin + f * range;
    const y = padTop + chartH - f * chartH;
    return { y, label: fmt(val) };
  });

  const last = pts[pts.length - 1];
  const first = pts[0];
  const font = "'DM Sans', -apple-system, sans-serif";

  return (
    <svg viewBox={`0 0 ${svgW} ${height}`} style={{ width: '100%', height }} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.12" />
          <stop offset="100%" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>
      {gridLines.map((g, i) => (
        <g key={i}>
          <line x1={labelW} y1={g.y} x2={labelW + chartW} y2={g.y} stroke="#f0f0f0" strokeWidth="1" />
          <text x={labelW - 6} y={g.y + 3.5} textAnchor="end" fontSize="9" fill="#b0b0b0" fontFamily={font}>{g.label}</text>
        </g>
      ))}
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last.x} cy={last.y} r="3" fill="#fff" stroke={color} strokeWidth="2" />
      <text x={last.x} y={last.y - 8} textAnchor="middle" fontSize="10" fontWeight="600" fill={color} fontFamily={font}>
        {fmt(last.value)}{unit ? ` ${unit}` : ''}
      </text>
      <text x={labelW} y={height - 3} fontSize="9" fill="#c0c0c0" fontFamily={font}>{fmtDate(first.date)}</text>
      <text x={labelW + chartW} y={height - 3} textAnchor="end" fontSize="9" fill="#c0c0c0" fontFamily={font}>{fmtDate(last.date)}</text>
    </svg>
  );
}
